import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Circle, Diamond, Square } from "lucide-react";

import { PROGRAMME_CONTACT_HREF } from "@/domains/certifications/constants";
import {
  certificationPaths,
  FROM_OVERVIEW_STATE,
} from "@/domains/certifications/routes";
import {
  AboutCard,
  AboutProgrammeProps,
} from "@/features/certifications/overview/components/types";

import "./styles.css";

const ICON_SIZE = 14;

const AboutProgramme = ({ slug, contentKey }: AboutProgrammeProps) => {
  const { t } = useTranslation();

  const cards: AboutCard[] = [
    {
      key: "gain",
      titleKey: "certification.details.gainsTitle",
      icon: <Circle size={ICON_SIZE} />,
      tone: "primary",
      to: certificationPaths.details(slug),
    },
    {
      key: "howItWorks",
      titleKey: "certification.overview.about.howItWorks.title",
      icon: <Square size={ICON_SIZE} />,
      tone: "info",
      to: certificationPaths.details(slug),
    },
    {
      key: "help",
      titleKey: "certification.labels.needHelp",
      icon: <Diamond size={ICON_SIZE} fill="currentColor" />,
      tone: "warning",
      to: null,
      href: PROGRAMME_CONTACT_HREF,
    },
  ];

  return (
    <section className="flex flex-col gap-400">
      <h2 className="certification-heading header-l-medium">
        {t("certification.overview.about.title")}
      </h2>

      <div className="certification-card-row flex flex-wrap gap-300">
        {cards.map((card) => {
          const items = t(`${contentKey}.about.${card.key}.items`, {
            returnObjects: true,
          });

          return (
            <article
              key={card.key}
              className={`about-card about-card-${card.tone} flex flex-col gap-300`}
            >
              <div className="flex items-center gap-200">
                <span className="about-card-icon flex-inline items-center justify-center">
                  {card.icon}
                </span>

                <h3 className="about-card-title body-m-medium">
                  {t(card.titleKey)}
                </h3>
              </div>

              {Array.isArray(items) && (
                <ul className="about-card-items body-s-regular flex flex-col gap-100">
                  {(items as string[]).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}

              {card.href && (
                <a
                  href={card.href}
                  className="certification-link about-card-link body-s-medium"
                >
                  {t(`certification.overview.about.${card.key}.link`)}
                </a>
              )}

              {card.to && (
                <Link
                  to={card.to}
                  state={FROM_OVERVIEW_STATE}
                  className="certification-link about-card-link body-s-medium"
                >
                  {t(`certification.overview.about.${card.key}.link`)}
                </Link>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default AboutProgramme;
